/**
 * Base API Client
 * Centralized HTTP client with error handling, token injection, and request cancellation
 */

import { HttpError, HttpResponse, RequestConfig, QueryParams } from './httpTypes';
import { withQuery } from './endpoints';
import { getAuthToken, setInMemoryToken } from './auth';

// Get base URL from environment
// React Scripts injects env vars at build time
declare const process: {
  env: {
    REACT_APP_API_BASE_URL?: string;
    [key: string]: string | undefined;
  };
};

// Runtime override – set by the widget mount function so embedded instances
// can point at a different API server without a rebuild.
let _runtimeBaseUrl: string | null = null;

/**
 * Override the API base URL at runtime.
 * Takes priority over the REACT_APP_API_BASE_URL env var.
 */
export function setApiBaseUrl(url: string): void {
  _runtimeBaseUrl = url.replace(/\/+$/, ''); // strip trailing slashes
}

const getBaseUrl = (): string => {
  if (_runtimeBaseUrl) return _runtimeBaseUrl;
  // @ts-ignore - process.env is injected by react-scripts
  return process.env.REACT_APP_API_BASE_URL || '/api/v1';
};

/**
 * Token refresh handler
 * This should be implemented based on your auth system
 */
let tokenRefreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  tokenRefreshPromise = (async (): Promise<string | null> => {
    try {
      const baseUrl = getBaseUrl();
      // Use raw fetch to avoid re-entering the apiClient wrapper (which would
      // trigger another 401 → refresh loop).
      // eslint-disable-next-line no-restricted-syntax
      const response = await fetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return null;
      const data = (await response.json()) as { access_token?: string };
      const token = data.access_token;
      if (token) {
        setInMemoryToken(token);
        return token;
      }
      return null;
    } catch {
      return null;
    } finally {
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}

/**
 * Create default headers for requests
 */
function createHeaders(customHeaders?: HeadersInit): HeadersInit {
  const language = localStorage.getItem('app_language') || 'en';
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept-Language': language,
    ...customHeaders,
  };

  // Add auth token if available
  const token = getAuthToken();
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Create AbortController with timeout
 */
function createAbortController(timeout?: number): AbortController {
  const controller = new AbortController();
  
  if (timeout) {
    setTimeout(() => controller.abort(), timeout);
  }
  
  return controller;
}

/**
 * Check if body is FormData
 */
function isFormData(body: unknown): body is FormData {
  return body instanceof FormData;
}

/**
 * Normalize error response
 */
async function normalizeError(response: Response): Promise<HttpError> {
  let errorData: unknown;
  
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      errorData = await response.json();
    } else {
      errorData = await response.text();
    }
  } catch {
    errorData = undefined;
  }
  
  return HttpError.fromResponse(response, errorData);
}

/**
 * Make HTTP request with error handling and token refresh
 */
async function request<T = unknown>(
  endpoint: string,
  method: string,
  body?: unknown,
  config?: RequestConfig,
  retryCount = 0
): Promise<HttpResponse<T>> {
  const baseUrl = getBaseUrl();
  const url = endpoint.startsWith('http://') || endpoint.startsWith('https://')
    ? endpoint
    : `${baseUrl}${endpoint}`;
  
  // Determine content type
  const headers = isFormData(body)
    ? { ...config?.headers } // Don't set Content-Type for FormData, browser will set it with boundary
    : createHeaders(config?.headers);
  
  // Create abort controller
  const abortController = config?.signal 
    ? { signal: config.signal } 
    : createAbortController(config?.timeout || 30000);
  
  const signal = abortController instanceof AbortController ? abortController.signal : abortController.signal;
  
  try {
    // eslint-disable-next-line no-restricted-syntax
    const response = await fetch(url, {
      method,
      headers,
      body: isFormData(body) ? body : body ? JSON.stringify(body) : undefined,
      signal,
      credentials: config?.credentials ?? 'same-origin',
    });

    // Handle 401 Unauthorized - try token refresh once
    if (response.status === 401 && retryCount === 0) {
      const newToken = await refreshToken();
      if (newToken) {
        // Retry request with new token
        const retryHeaders = isFormData(body)
          ? { ...config?.headers, Authorization: `Bearer ${newToken}` }
          : createHeaders({ ...config?.headers, Authorization: `Bearer ${newToken}` });
        
        // eslint-disable-next-line no-restricted-syntax
        const retryResponse = await fetch(url, {
          method,
          headers: retryHeaders,
          body: isFormData(body) ? body : body ? JSON.stringify(body) : undefined,
          signal,
          credentials: config?.credentials ?? 'same-origin',
        });

        if (!retryResponse.ok) {
          throw await normalizeError(retryResponse);
        }

        return await parseResponse<T>(retryResponse);
      }
    }

    if (!response.ok) {
      throw await normalizeError(response);
    }

    return await parseResponse<T>(response);
  } catch (error) {
    if (error instanceof HttpError) {
      throw error;
    }
    
    // Handle network errors, timeouts, etc.
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new HttpError(0, 'Request Timeout', 'Request timed out or was aborted');
      }
      throw new HttpError(0, 'Network Error', error.message);
    }
    
    throw HttpError.fromError(error);
  }
}

/**
 * Parse response based on content type
 */
async function parseResponse<T>(response: Response): Promise<HttpResponse<T>> {
  // 204/205 — success with no body (e.g. DELETE document).
  if (response.status === 204 || response.status === 205) {
    return {
      data: undefined as T,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
  }

  let data: T;
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const text = await response.text();
    data = (text.trim() ? JSON.parse(text) : undefined) as T;
  } else if (
    contentType.includes('application/octet-stream') ||
    contentType.includes('application/pdf') ||
    contentType.includes('application/vnd.openxmlformats') ||
    contentType.includes('image/') ||
    contentType.includes('text/csv') ||
    contentType.includes('application/csv')
  ) {
    data = (await response.blob()) as unknown as T;
  } else {
    data = (await response.text()) as unknown as T;
  }

  return {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  };
}

/**
 * GET request
 */
export async function get<T = unknown>(
  endpoint: string,
  query?: QueryParams,
  config?: RequestConfig
): Promise<HttpResponse<T>> {
  const url = withQuery(endpoint, query);
  return request<T>(url, 'GET', undefined, config);
}

/**
 * Load JSON from the app origin (e.g. files in `public/`). Does not use the API base URL.
 */
export async function getPublicJson<T = unknown>(path: string): Promise<T> {
  const url = path.startsWith('/') ? path : `/${path}`;
  // eslint-disable-next-line no-restricted-syntax
  const response = await fetch(url);
  if (!response.ok) {
    throw await normalizeError(response);
  }
  return response.json() as Promise<T>;
}

/**
 * POST request
 */
export async function post<TIn = unknown, TOut = unknown>(
  endpoint: string,
  body?: TIn,
  config?: RequestConfig
): Promise<HttpResponse<TOut>> {
  return request<TOut>(endpoint, 'POST', body, config);
}

/**
 * PUT request
 */
export async function put<TIn = unknown, TOut = unknown>(
  endpoint: string,
  body?: TIn,
  config?: RequestConfig
): Promise<HttpResponse<TOut>> {
  return request<TOut>(endpoint, 'PUT', body, config);
}

/**
 * PATCH request
 */
export async function patch<TIn = unknown, TOut = unknown>(
  endpoint: string,
  body?: TIn,
  config?: RequestConfig
): Promise<HttpResponse<TOut>> {
  return request<TOut>(endpoint, 'PATCH', body, config);
}

/**
 * DELETE request
 */
export async function del<T = unknown>(
  endpoint: string,
  config?: RequestConfig
): Promise<HttpResponse<T>> {
  return request<T>(endpoint, 'DELETE', undefined, config);
}

/**
 * Upload file (multipart/form-data)
 */
export async function upload<T = unknown>(
  endpoint: string,
  formData: FormData,
  config?: RequestConfig
): Promise<HttpResponse<T>> {
  return request<T>(endpoint, 'POST', formData, {
    ...config,
    headers: {
      // Don't set Content-Type for FormData
      ...config?.headers,
    },
  });
}

/**
 * Download file as blob
 */
export async function download(
  endpoint: string,
  query?: QueryParams,
  config?: RequestConfig
): Promise<Blob> {
  const url = withQuery(endpoint, query);
  const response = await request<Blob>(url, 'GET', undefined, {
    ...config,
  });
  return response.data;
}

// Re-export auth functions for convenience
export { setAuthToken, getAuthToken as getAuthTokenFromStorage, clearAuth, isAuthenticated } from './auth';

